import React, { useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon } from '@heroicons/react/24/outline';
import { useTheme } from '../contexts/ThemeContext';

interface ImageLightboxProps {
  isOpen: boolean;
  imageUrl: string;
  onClose: () => void;
  alt?: string;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ isOpen, imageUrl, onClose, alt = 'Image' }) => {
  const { isDark } = useTheme();
  const [zoom, setZoom] = React.useState(1);

  useEffect(() => {
    // Reset zoom when opening new image
    if (isOpen) {
      setZoom(1);
    }
  }, [isOpen, imageUrl]);

  useEffect(() => {
    // Handle escape key to close
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    // Prevent body scroll when lightbox is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-90 backdrop-blur-sm"
      onClick={handleBackgroundClick}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
        title="Fechar (Esc)"
      >
        <XMarkIcon className="h-6 w-6 text-gray-800 dark:text-white" />
      </button>

      {/* Zoom controls */}
      <div className="absolute top-4 left-4 flex gap-2 z-10">
        <button
          onClick={handleZoomOut}
          disabled={zoom <= 0.5}
          className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Diminuir zoom"
        >
          <MagnifyingGlassMinusIcon className="h-6 w-6 text-gray-800 dark:text-white" />
        </button>
        <button
          onClick={handleZoomIn}
          disabled={zoom >= 3}
          className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Aumentar zoom"
        >
          <MagnifyingGlassPlusIcon className="h-6 w-6 text-gray-800 dark:text-white" />
        </button>
        <div className="px-3 py-2 bg-white dark:bg-gray-800 rounded-full shadow-lg text-sm font-medium text-gray-800 dark:text-white">
          {Math.round(zoom * 100)}%
        </div>
      </div>

      {/* Image container */}
      <div className="w-full h-full flex items-center justify-center p-4 overflow-auto">
        <img
          src={imageUrl}
          alt={alt}
          className="max-w-full max-h-full object-contain transition-transform duration-200 cursor-zoom-in"
          style={{ 
            transform: `scale(${zoom})`,
            transformOrigin: 'center center'
          }}
          onClick={(e) => {
            e.stopPropagation();
            if (zoom < 3) {
              handleZoomIn();
            }
          }}
        />
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 px-4 py-2 bg-white dark:bg-gray-800 rounded-full shadow-lg text-sm text-gray-600 dark:text-gray-300">
        Clique na imagem para zoom • ESC para fechar
      </div>
    </div>
  );
};

export default ImageLightbox;
